import axios from 'axios'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL } from '../../constants/system'
import { extractErrorMessage } from '../../utils/apitools'
import useAccount from '../useAccount'

/** One post the file listed more than once for a team (`types.InfluencerDuplicate`). */
export interface InfluencerDuplicate {
  dashboard: string
  contentId: string
  profileName: string
  listings: number
  activities: string[]
  teams: string[]
}

/** One dashboard's share of an upload (`types.InfluencerDashboardLine`). */
export interface InfluencerDashboardLine {
  dashboard: string
  posts: number
  paidPosts: number
  influencers: number
  reach: number
  views: number
  engagements: number
  from: string
  to: string
  new: number
  updated: number
  unchanged: number
  heldNotInFile: number
}

/**
 * What an influencer upload reports (`types.InfluencerUploadSummary`), the same
 * for the preview and for the write. `written` is false on a preview.
 */
export interface InfluencerUploadSummary {
  fileName: string
  written: boolean
  rows: number
  posts: number
  duplicateListings: number
  duplicatePosts: number
  duplicates: InfluencerDuplicate[]
  conflicts: number
  errorCells: number
  crossDashboard: number
  from: string
  to: string
  new: number
  updated: number
  unchanged: number
  heldNotInFile: number
  fileTeams: Array<{ team: string; dashboard: string; rows: number }>
  dashboards: InfluencerDashboardLine[]
  /** What the upload means for the Apify scrape queue (21 Oct 2026). */
  scrape?: InfluencerScrapePlan
}

/** The file's posts by site, which can be scraped, and how many were queued. */
export interface InfluencerScrapePlan {
  posts: number
  scrapable: number
  unsupported: number
  sites: Array<{ site: string; posts: number; scrapable: boolean }>
  /** Newly queued by this upload; 0 on a preview and for posts already queued. */
  queued: number
}

/** One written upload (`types.InfluencerUpload`). */
export interface InfluencerUpload {
  id: number
  fileName: string
  uploadedBy: string
  uploadedAt: number
  summary: InfluencerUploadSummary
}

const UPLOAD_URL = `${API_BASE_URL}/sky-social-media/influencers/upload`
const HISTORY_URL = `${API_BASE_URL}/sky-social-media/influencers/uploads`
const HISTORY_KEY = ['sky_social', 'influencer_uploads'] as const

/**
 * Sends the file. Without `confirm` the server previews and writes nothing;
 * with it, the same file is written (goapi/pkg/server/sky_influencer_upload.go).
 */
export async function sendInfluencerFile(file: File, confirm: boolean): Promise<InfluencerUploadSummary> {
  const form = new FormData()
  form.append('file', file)
  try {
    const res = await axios.post<InfluencerUploadSummary>(UPLOAD_URL, form, {
      params: confirm ? { confirm: '1' } : {},
    })
    return res.data
  } catch (e) {
    throw new Error(extractErrorMessage(e) || (confirm ? 'The upload was not written.' : 'The file could not be read.'))
  }
}

/** The last 20 written uploads, newest first. Admins only. */
export function useInfluencerUploadHistory() {
  const account = useAccount()
  const query = useQuery({
    queryKey: HISTORY_KEY,
    enabled: Boolean(account.user),
    queryFn: async (): Promise<InfluencerUpload[]> => {
      try {
        const res = await axios.get<{ uploads: InfluencerUpload[] }>(HISTORY_URL)
        return res.data.uploads ?? []
      } catch (e) {
        throw new Error(extractErrorMessage(e) || 'The upload history could not be read.')
      }
    },
  })
  return { uploads: query.data ?? [], isLoading: query.isLoading, error: query.error as Error | null }
}

/**
 * After a write: the history gains a row and every dashboard's influencer
 * figures may have moved.
 */
export function useInvalidateAfterInfluencerUpload() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: HISTORY_KEY })
    queryClient.invalidateQueries({ queryKey: ['sky_social', 'influencers'] })
  }
}
